import type { PatchRelease } from "../types";

/** Initial release — everything through the public alpha before daily patch notes. */
export const release: PatchRelease = {
  id: "2026-06-01_01",
  version: "Initial",
  date: "Through 1 Jun 2026",
  sinceCommit: "58ede19",
  gameplay: [
    {
      text: "**Warlocks — Arena of the Void** launches: top-down knockback arena on a shrinking platform. Shove rivals into the void; last wizard standing wins the round. Most round wins after 10 rounds takes the match (host can change round count).",
    },
    {
      text: "Knockback scales from spell damage — not set directly. Lower HP ⇒ bigger launches. 1000 HP pool: chip damage matters, but getting knocked off the rim is the main threat. HP reaching 0 from any source eliminates you.",
    },
    {
      text: "Fixed spell slots (keys 1 2 3 4 5 Q W E): one spell per column. Start with Fireball + Scourge; buying into a slot commits that spell and locks out the other options in that column.",
    },
    {
      text: "Shop between rounds: spend gold on spells (unlock + upgrades), then passive items. Starting gold is host-configurable. Gold each round from finishing position (+ kill bonus); shop auto-advances after 60s unless everyone readies.",
    },
    {
      text: "Nine passive items: Boots of Speed, Lava Treads, Pendant, Cape, Helmet, Blood Sword, Stone of Jordan, Pocket Watch, Staff of Fireball.",
    },
    {
      text: "Arena shrinks in stages with pauses between steps (not a smooth continuous squeeze). World size is fixed; zoom adapts to your screen.",
    },
    {
      text: "**All Random** mode: random spell and item grants each round on a shared budget schedule.",
    },
    {
      text: "**Teams**: lobby team assignment; same-team players have no friendly fire.",
    },
    {
      text: "Online multiplayer with 4-character room codes and reconnect tokens if you drop.",
    },
    {
      text: "Authoritative Go simulation on the server; your wizard is predicted locally via WebAssembly for responsive movement.",
    },
    {
      text: "Custom keybinds for every spell slot.",
    },
    {
      text: "Synth sound effects with a mute toggle.",
    },
    {
      text: "Combat statistics: damage dealt/taken, heals, kills, and per-spell breakdowns.",
    },
    {
      text: "Projectiles can collide with each other mid-flight.",
    },
    {
      text: "Major balance pass: ~4× spell damage across the board; knockback constant tamed (KnockPerDamage 30 → 8) so hits stay lethal without absurd launches. Cooldowns lengthened; projectiles sped up (~1.5–1.7×). Shield knockback-immunity window trimmed; Cape and Pendant outliers reined in.",
    },
    {
      text: "Wizards face their movement direction (sprite mirrors left/right, not full rotation). Larger character models.",
    },
  ],
  skills: [
    {
      spellId: "scourge",
      changes: [{
        text: "Channel 1.5s with movement locked, then explode for heavy knockback — hurts you too (120 self-damage at base).",
      }],
    },
    {
      spellId: "meteor",
      changes: [{
        text: "Reworked as an aerial strike: telegraph only in the sky, then crashes down for a large AoE blast (96 detonation damage, 200-radius).",
      }],
    },
    {
      spellId: "glacier",
      changes: [{
        text: "New spell (slot 1, replaces Fireball): slow heavy ice spear — 58 damage, 2.8s chill on hit.",
      }],
    },
    {
      spellId: "chain",
      changes: [{
        text: "New spell (slot 3): Chain Spark hitscan — arcs to up to 3 nearby enemies with 0.72 damage falloff per hop.",
      }],
    },
    {
      spellId: "frozenorb",
      changes: [{
        text: "New ultimate (slot 4): spinning orb sprays 8 frost spikes every 0.2s while rolling; heavy impact + chill.",
      }],
    },
    {
      spellId: "barrier",
      changes: [{
        text: "New spell (slot E): Arcane Barrier absorbs up to 260 damage over 5s (knockback still applies).",
      }],
    },
    {
      spellId: "reflect",
      changes: [{
        text: "New spell (slot E): Reflect Shield — bounce incoming projectiles back at their caster for 2s.",
      }],
    },
    {
      spellId: "snare",
      changes: [{
        text: "New spell (slot W): Sigil Snare — plant a rune that roots and chills enemies who linger inside after arming.",
      }],
    },
    {
      spellId: "firewall",
      changes: [{
        text: "New spell (slot W): conjure a wall of fire crossways to your aim; enemies in the flames take heavy damage and keep burning after leaving.",
      }],
    },
    {
      spellId: "gravity",
      changes: [{
        text: "Reworked into **Black Hole**: ground snare that drags every enemy in a wide radius toward its core (was a pull projectile).",
      }],
    },
    {
      spellId: "windwalk",
      changes: [{
        text: "Touch slam added: first enemy contact deals 80 damage and breaks stealth.",
      }],
    },
  ],
};
